/**
 * DrawBoundaryWizard — Guided boundary drawing for simple mode.
 * When drawing starts, the modal steps aside so the map receives clicks.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Paper,
  Stack,
  IconButton,
  Chip,
  alpha,
} from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowForward from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { endTaskWizard, addNotification } from '../../store/slices/uiSlice';
import { setActiveTool as setDrawingTool } from '../../store/slices/drawingSlice';
import { useGeoveraTheme } from '../../theme/ThemeContext';

interface DrawBoundaryWizardProps {
  open: boolean;
  onClose: () => void;
}

const STEPS = [
  "Understand what we're doing",
  'Start drawing on the map',
  'Finish your boundary',
  'Name it and save',
];

const DrawBoundaryWizard: React.FC<DrawBoundaryWizardProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { palette } = useGeoveraTheme();
  const drawingTool = useAppSelector((s) => s.drawing.activeTool);
  const drawings = useAppSelector((s) => s.drawing.drawings);
  const [activeStep, setActiveStep] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  /** When true, full dialog is hidden so the map is fully clickable */
  const [mapFocused, setMapFocused] = useState(false);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setIsDrawing(false);
      setMapFocused(false);
    }
  }, [open]);

  // If user finished a polygon while map-focused, advance wizard
  useEffect(() => {
    if (!open || !mapFocused || !isDrawing) return;
    if (drawingTool === null && drawings.length > 0) {
      setMapFocused(false);
      setActiveStep(2);
    }
  }, [open, mapFocused, isDrawing, drawingTool, drawings.length]);

  const handleNext = () => {
    if (activeStep === 1 && !isDrawing) {
      startDrawing();
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setMapFocused(false);
  };

  const startDrawing = () => {
    dispatch(setDrawingTool('polygon'));
    setIsDrawing(true);
    setMapFocused(true);
    dispatch(
      addNotification({
        type: 'info',
        message: 'Drawing mode on — click corners on the map, double-click to finish',
      })
    );
  };

  const handleFinish = () => {
    dispatch(endTaskWizard());
    dispatch(setDrawingTool(null));
    dispatch(
      addNotification({
        type: 'success',
        message: 'Great job! Your boundary is ready. You can edit it anytime.',
      })
    );
    onClose();
  };

  const handleClose = () => {
    dispatch(endTaskWizard());
    dispatch(setDrawingTool(null));
    setMapFocused(false);
    setIsDrawing(false);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Let&apos;s draw the outline of your land or project
            </Typography>
            <Typography paragraph>
              You&apos;ll click points on the map to create a shape (like connecting dots).
            </Typography>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body2">
                  <strong>Example:</strong> Mark a paddock by clicking the corners of the fence
                  line. You can adjust it later.
                </Typography>
              </CardContent>
            </Card>
            <Alert severity="info">
              After you start drawing, this window steps aside so you can click the map freely.
            </Alert>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to draw on the map?
            </Typography>
            <Typography paragraph>
              1. Press <strong>Start Drawing</strong>
              <br />
              2. Click each corner of your area
              <br />
              3. Double-click the last point to finish
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Edit />}
              onClick={startDrawing}
              sx={{ mt: 1 }}
            >
              Start Drawing on the Map
            </Button>
            {isDrawing && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Drawing mode is active. Use the bar at the bottom of the map if you need help.
              </Alert>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Almost done — finish your shape
            </Typography>
            <Typography paragraph>
              Double-click the last point to close the outline. If you need more points, press
              Continue Drawing.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={startDrawing}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Continue Drawing
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CheckCircle color="success" />
              <Typography>Your shape should now appear filled on the map.</Typography>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              You&apos;re all set
            </Typography>
            <Typography paragraph>
              Your boundary is saved as a drawing on the map. Measure it, include it in a report, or
              switch to Advanced mode for full layer tools.
            </Typography>
            <Alert severity="success">
              Tip: open <strong>Save project</strong> from the simple guide to store your work in
              the cloud.
            </Alert>
          </Box>
        );
      default:
        return null;
    }
  };

  // Compact floating bar while user draws on the map (dialog must not block clicks)
  if (open && mapFocused) {
    return (
      <Paper
        elevation={10}
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 72,
          transform: 'translateX(-50%)',
          zIndex: 1300,
          px: 2,
          py: 1.25,
          minWidth: { xs: 'min(360px, calc(100% - 24px))', sm: 420 },
          maxWidth: 'calc(100% - 24px)',
          bgcolor: alpha(palette.bg.elevated, 0.98),
          border: `1px solid ${palette.border.base}`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <Chip size="small" color="success" label="Drawing" sx={{ fontWeight: 800 }} />
          <Typography sx={{ flex: 1, fontSize: '0.9rem', fontWeight: 600 }}>
            Click corners on the map · double-click to finish
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setMapFocused(false);
              setActiveStep(2);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            I&apos;m done
          </Button>
          <IconButton size="small" onClick={handleClose} aria-label="Cancel drawing">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Draw Your Boundary — Step {activeStep + 1} of {STEPS.length}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        {activeStep < STEPS.length - 1 && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress variant="determinate" value={((activeStep + 1) / STEPS.length) * 100} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForward />}
            disabled={activeStep === 1 && !isDrawing}
          >
            {activeStep === 1 && !isDrawing ? 'Start first' : 'Next'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleFinish} color="success">
            Finish
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DrawBoundaryWizard;
